import React, {
  createContext,
  useContext,
  useState,
  useEffect,
  useCallback,
  useRef,
  useMemo,
} from "react";
import { useLocation } from "react-router-dom";
import { getLivisToken } from "../helpers/storage";
import {
  getAllNotificationsAPI,
  markNotificationReadAPI,
  markAllReadAPI,
  clearAllNotificationsAPI,
  getOrganizationPreferencesAPI,
  toggleNotificationCategoryAPI,
  toggleNotificationSubcategoryAPI,
  addRecipientToCategoryAPI,
  getRecipientsFromCategoryAPI,
  removeRecipientsFromCategoryAPI,
  importRecipientsFromCategoryAPI,
  toggleNotificationStarAPI,
  getLivisUsersAPI,
  createAlertTriggersAPI,
  editAlertTriggersAPI,
  pauseAlertTriggersAPI,
  getAlertTriggerPreferencesAPI,
  getAllAlertsAPI,
  updateReportDigestFrequencyAPI,
} from "../services/notificationService";
import { livisAlert } from "../helpers/alert";
import { t } from "i18next";
import {
  useHeaderNotificationsQuery,
  useAllAlertsQuery,
} from "../modules/notifications/hooks/queries/useNotificationQueries";
import { useQueryClient } from "@tanstack/react-query";
import { notificationKeys } from "../modules/notifications/hooks/queryKeys/notificationKeys";

// --- Interfaces ---
export interface IAlertTrigger {
  is_enabled: boolean;
  channels: string[];
  severity: "low" | "medium" | "high" | "critical";
  thresholds_value?: number[];
  thresholds_days?: number[];
  thresholds_percent?: number[];
  operator: string[];
  user_groups: any[];
}

interface ChannelStatusDetail {
  status: string;
  is_read?: boolean;
  read_at?: string | null;
}

interface ChannelStatus {
  in_app?: ChannelStatusDetail;
  slack?: ChannelStatusDetail;
  email?: ChannelStatusDetail;
}

export interface INotification {
  _id: string;
  notification_type: "success" | "error" | "warning" | "info";
  category: string;
  event_type: string;
  title: string;
  message: string;
  priority: "low" | "medium" | "high";
  created_at: string;
  action_url: string | null;
  channel_status: ChannelStatus;
  is_starred?: boolean;
}

interface NotificationContextType {
  headerNotifications: INotification[];
  unreadCount: number;
  isLoadingGlobal: boolean;
  allAlerts: INotification[];
  // ✅ FIX: Expose dismissed set and dismiss action so banner
  // dismissals persist across page navigation (component remounts)
  dismissedAlertIds: Set<string>;
  dismissAlert: (id: string) => void;
  fetchHeaderData: () => Promise<void>;
  fetchAllAlerts: () => Promise<void>;
  markAsRead: (id: string) => Promise<void>;
  markAllAsRead: () => Promise<void>;
  clearAll: () => Promise<void>;
  fetchPageData: (
    params: any
  ) => Promise<{ notifications: INotification[]; total: number }>;
  getPreferences: (orgName: string) => Promise<any>;
  toggleCategory: (orgName: string, category: string) => Promise<void>;
  toggleSubcategory: (
    orgName: string,
    category: string,
    subKey: string
  ) => Promise<void>;
  addRecipient: (
    orgName: string,
    category: string,
    recipients: any[]
  ) => Promise<any>;
  getRecipients: (
    orgName: string,
    category: string,
    isLivisUser?: boolean
  ) => Promise<any>;
  removeRecipients: (
    orgName: string,
    category: string,
    emails: string[]
  ) => Promise<void>;
  importRecipients: (
    orgName: string,
    sourceCategory: string,
    targetCategory: string
  ) => Promise<void>;
  toggleStar: (id: string) => Promise<void>;
  getLivisUsers: (orgName: string) => Promise<any[]>;
  createAlertTrigger: (payload: any) => Promise<void>;
  editAlertTrigger: (payload: any) => Promise<void>;
  pauseAlertTrigger: (orgName: string, event: string) => Promise<void>;
  getAlertTriggerPreferences: (orgName: string) => Promise<Record<string, IAlertTrigger>>;
  updateReportDigestFrequency: (orgName: string, frequencies: string[]) => Promise<void>;
  resetAlertState: () => void;
}

// Stable actions context — only contains useCallback functions, never re-renders consumers
interface NotificationActionsContextType {
  getPreferences: (orgName: string) => Promise<any>;
  toggleCategory: (orgName: string, category: string) => Promise<void>;
  toggleSubcategory: (
    orgName: string,
    category: string,
    subKey: string
  ) => Promise<void>;
  addRecipient: (
    orgName: string,
    category: string,
    recipients: any[]
  ) => Promise<any>;
  getRecipients: (
    orgName: string,
    category: string,
    isLivisUser?: boolean
  ) => Promise<any>;
  removeRecipients: (
    orgName: string,
    category: string,
    emails: string[]
  ) => Promise<void>;
  importRecipients: (
    orgName: string,
    sourceCategory: string,
    targetCategory: string
  ) => Promise<void>;
  getLivisUsers: (orgName: string) => Promise<any[]>;
  createAlertTrigger: (payload: any) => Promise<void>;
  editAlertTrigger: (payload: any) => Promise<void>;
  pauseAlertTrigger: (orgName: string, event: string) => Promise<void>;
  getAlertTriggerPreferences: (
    orgName: string
  ) => Promise<Record<string, IAlertTrigger>>;
}

const NotificationActionsContext = createContext<
  NotificationActionsContextType | undefined
>(undefined);
const NotificationContext = createContext<NotificationContextType | undefined>(
  undefined
);

export const NotificationProvider: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  // const [headerNotifications, setHeaderNotifications] = useState<INotification[]>([]);
  // const [allAlerts, setAllAlerts] = useState<INotification[]>([]);
  // const [unreadCount, setUnreadCount] = useState<number>(0);
  // const [isLoadingGlobal, setIsLoadingGlobal] = useState<boolean>(false);
  const location = useLocation();
  const queryClient = useQueryClient();

  // const hasFetchedAlerts = useRef(false);

  const headerQuery = useHeaderNotificationsQuery();
  const headerNotifications = headerQuery.data?.notifications ?? [];
  const unreadCount = headerNotifications.filter(
    (n: any) => !(n.is_read ?? n.channel_status?.in_app?.is_read)
  ).length;

  // ✅ Replaces fetchAllAlerts + hasFetchedAlerts ref + alerts useEffect
  const alertsQuery = useAllAlertsQuery();
  const allAlerts = alertsQuery.data ?? [];
  const isLoadingGlobal = headerQuery.isLoading || alertsQuery.isLoading;

  // ✅ FIX: useRef so the dismissed set is never wiped on re-render or remount.
  // A plain useState would reset every time TrialAlertBanner unmounts (page change).
  const dismissedAlertIdsRef = useRef<Set<string>>(new Set());
  // A small counter state just to trigger a re-render when the ref mutates,
  // so the banner reacts immediately after dismissal.

  // After the dismissedAlertIdsRef declaration, add:
  const resetAlertState = useCallback(() => {
    queryClient.removeQueries({ queryKey: notificationKeys.alerts() });
    queryClient.removeQueries({ queryKey: notificationKeys.header() });
    dismissedAlertIdsRef.current = new Set();
    forceUpdate((n) => n + 1);
  }, [queryClient]);

  const [, forceUpdate] = useState(0);

  const dismissAlert = useCallback((id: string) => {
    dismissedAlertIdsRef.current = new Set(dismissedAlertIdsRef.current).add(
      id
    );
    forceUpdate((n) => n + 1);
  }, []);

  // const consecutiveFailuresRef = useRef(0);

  const fetchHeaderData = useCallback(async () => {
    await queryClient.invalidateQueries({
      queryKey: notificationKeys.header(),
    });
  }, [queryClient]);

  const fetchAllAlerts = useCallback(async () => {
    await queryClient.invalidateQueries({
      queryKey: notificationKeys.alerts(),
    });
  }, [queryClient]);

  const fetchPageData = useCallback(async (params: any) => {
    const response = await getAllNotificationsAPI(params);
    return {
      notifications: response.data.data.notifications,
      total: response.data.data.total,
    };
  }, []);

  const markAsRead = useCallback(
    async (id: string) => {
      await markNotificationReadAPI(id);
      // Optimistically update header list
      queryClient.setQueryData(notificationKeys.header(), (old: any) => {
        if (!old) return old;
        return {
          ...old,
          notifications: old.notifications.map((n: any) =>
            n._id === id
              ? {
                  ...n,
                  is_read: true,
                  channel_status: {
                    ...n.channel_status,
                    in_app: { ...n.channel_status?.in_app, is_read: true },
                  },
                }
              : n
          ),
        };
      });

      // Optimistically update all paged notification caches
      queryClient.setQueriesData(
        { queryKey: notificationKeys.pageAll() },
        (old: any) => {
          if (!old?.notifications) return old;
          return {
            ...old,
            notifications: old.notifications.map((n: any) =>
              n._id === id
                ? {
                    ...n,
                    is_read: true,
                    channel_status: {
                      ...n.channel_status,
                      in_app: { ...n.channel_status?.in_app, is_read: true },
                    },
                  }
                : n
            ),
          };
        }
      );

      // Revalidate all notification caches for consistency across filters/pagination
      queryClient.invalidateQueries({ queryKey: notificationKeys.header() });
      queryClient.invalidateQueries({ queryKey: notificationKeys.pageAll() });
    },
    [queryClient]
  );

  const markAllAsRead = useCallback(async () => {
    await markAllReadAPI();
    // Optimistically update header list
    queryClient.setQueryData(notificationKeys.header(), (old: any) => {
      if (!old?.notifications) return old;
      return {
        ...old,
        notifications: old.notifications.map((n: any) => ({
          ...n,
          is_read: true,
          channel_status: {
            ...n.channel_status,
            in_app: { ...n.channel_status?.in_app, is_read: true },
          },
        })),
      };
    });

    // Optimistically update all paged notification caches
    queryClient.setQueriesData(
      { queryKey: notificationKeys.pageAll() },
      (old: any) => {
        if (!old?.notifications) return old;
        return {
          ...old,
          notifications: old.notifications.map((n: any) => ({
            ...n,
            is_read: true,
            channel_status: {
              ...n.channel_status,
              in_app: { ...n.channel_status?.in_app, is_read: true },
            },
          })),
        };
      }
    );

    // Ensure both header and notifications page refresh immediately
    queryClient.invalidateQueries({ queryKey: notificationKeys.header() });
    queryClient.invalidateQueries({ queryKey: notificationKeys.pageAll() });
  }, [queryClient]);

  const clearAll = useCallback(async () => {
    await clearAllNotificationsAPI();
    queryClient.setQueryData(notificationKeys.header(), (old: any) => {
      if (!old) return old;
      return { ...old, notifications: [] };
    });
  }, [queryClient]);

  // Settings & Preferences
  const getPreferences = useCallback(async (orgName: string) => {
    try {
      const resp = await getOrganizationPreferencesAPI(orgName);
      return resp.data.data;
    } catch (error: any) {
      if (error?.response?.status === 400) {
        livisAlert(
          `${t("No organization notification preference found")}`,
          "error"
        );
      }
      throw error;
    }
  }, []);

  const toggleCategory = useCallback(
    async (orgName: string, category: string) => {
      await toggleNotificationCategoryAPI({
        organization_name: orgName,
        category,
      });
    },
    []
  );

  const toggleSubcategory = useCallback(
    async (orgName: string, category: string, subKey: string) => {
      await toggleNotificationSubcategoryAPI({
        organization_name: orgName,
        category,
        subcategory_key: subKey,
      });
    },
    []
  );

  const addRecipient = useCallback(
    async (orgName: string, category: string, recipients: any[]) => {
      return await addRecipientToCategoryAPI({
        organization_name: orgName,
        category,
        recipient_data: recipients,
      });
    },
    []
  );

  const getRecipients = useCallback(
    async (orgName: string, category: string, isLivisUser?: boolean) => {
      return (
        await getRecipientsFromCategoryAPI({
          organization_name: orgName,
          category,
          is_livis_user: isLivisUser,
        })
      ).data.data;
    },
    []
  );

  const removeRecipients = useCallback(
    async (orgName: string, category: string, emails: string[]) => {
      await removeRecipientsFromCategoryAPI({
        organization_name: orgName,
        category,
        recipient_emails: emails,
      });
    },
    []
  );

  const importRecipients = useCallback(
    async (orgName: string, source: string, target: string) => {
      await importRecipientsFromCategoryAPI({
        organization_name: orgName,
        source_category: source,
        target_category: target,
      });
    },
    []
  );

  const toggleStar = useCallback(
    async (id: string) => {
      await toggleNotificationStarAPI(id);
      // Optimistically update header notifications
      queryClient.setQueryData(notificationKeys.header(), (old: any) => {
        if (!old) return old;
        return {
          ...old,
          notifications: old.notifications.map((n: any) =>
            n._id === id ? { ...n, is_starred: !n.is_starred } : n
          ),
        };
      });

      // Optimistically update all paged notification caches
      queryClient.setQueriesData(
        { queryKey: notificationKeys.pageAll() },
        (old: any) => {
          if (!old?.notifications) return old;
          return {
            ...old,
            notifications: old.notifications.map((n: any) =>
              n._id === id ? { ...n, is_starred: !n.is_starred } : n
            ),
          };
        }
      );

      // Revalidate to keep all filtered views in sync
      queryClient.invalidateQueries({ queryKey: notificationKeys.header() });
      queryClient.invalidateQueries({ queryKey: notificationKeys.pageAll() });
    },
    [queryClient]
  );

  const getLivisUsers = useCallback(async (orgName: string) => {
    return (await getLivisUsersAPI({ organization_name: orgName })).data.data
      .users;
  }, []);

  // Alert Trigger Implementations
  const createAlertTrigger = useCallback(async (payload: any) => {
    try {
      await createAlertTriggersAPI(payload);
    } catch (error: any) {
      const message = error?.response?.data?.message || "Operation failed";
      throw new Error(message);
    }
  }, []);

  const editAlertTrigger = useCallback(async (payload: any) => {
    try {
      await editAlertTriggersAPI(payload);
    } catch (error: any) {
      const message = error?.response?.data?.message || "Operation failed";
      throw new Error(message);
    }
  }, []);

  const pauseAlertTrigger = useCallback(
    async (orgName: string, event: string) => {
      await pauseAlertTriggersAPI({
        organization_name: orgName,
        trigger_event: event,
      });
    },
    []
  );

  const getAlertTriggerPreferences = useCallback(async (orgName: string) => {
    try {
      const resp = await getAlertTriggerPreferencesAPI(orgName);
      return resp.data.data;
    } catch (error: any) {
      if (error?.response?.status === 400) {
        livisAlert(`${t("No organization alert preference found")}`, "error");
      }
      throw error;
    }
  }, []);

  // Report Digest Frequency
  const updateReportDigestFrequency = useCallback(
    async (orgName: string, frequencies: string[]) => {
      try {
        await updateReportDigestFrequencyAPI({
          organization_name: orgName,
          report_frequency: frequencies,
        });
      } catch (error: any) {
        const message =
          error?.response?.data?.message ||
          "Failed to update report digest frequency";
        throw new Error(message);
      }
    },
    []
  );

  // Memoized stable actions — never re-creates because all deps are stable useCallbacks
  const stableActions = useMemo<NotificationActionsContextType>(
    () => ({
      getPreferences,
      toggleCategory,
      toggleSubcategory,
      addRecipient,
      getRecipients,
      removeRecipients,
      importRecipients,
      getLivisUsers,
      createAlertTrigger,
      editAlertTrigger,
      pauseAlertTrigger,
      getAlertTriggerPreferences,
    }),
    [
      getPreferences,
      toggleCategory,
      toggleSubcategory,
      addRecipient,
      getRecipients,
      removeRecipients,
      importRecipients,
      getLivisUsers,
      createAlertTrigger,
      editAlertTrigger,
      pauseAlertTrigger,
      getAlertTriggerPreferences,
    ]
  );

  return (
    <NotificationContext.Provider value={{
      headerNotifications, allAlerts, unreadCount, isLoadingGlobal,
      // ✅ FIX: Pass ref's current value and the dismiss action
      dismissedAlertIds: dismissedAlertIdsRef.current,
      dismissAlert,
      fetchHeaderData, fetchAllAlerts, markAsRead, markAllAsRead, clearAll, fetchPageData,
      getPreferences, toggleCategory, toggleSubcategory, addRecipient, getRecipients,
      removeRecipients, importRecipients, toggleStar, getLivisUsers,
      createAlertTrigger, editAlertTrigger, pauseAlertTrigger, getAlertTriggerPreferences,
      updateReportDigestFrequency, resetAlertState
    }}>
      {children}
    </NotificationContext.Provider>
  );
};

export const useNotification = () => {
  const context = useContext(NotificationContext);
  if (!context)
    throw new Error(
      "useNotification must be used within a NotificationProvider"
    );
  return context;
};

// Use this in components that only need API actions (e.g. Settings).
// It never re-renders from polling.
export const useNotificationActions = () => {
  const context = useContext(NotificationActionsContext);
  if (!context)
    throw new Error(
      "useNotificationActions must be used within a NotificationProvider"
    );
  return context;
};
